
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FixedJob, ScheduleByDay, DayOfWeek } from "@/types/fixed-job";
import { format } from "date-fns";
import { th } from "date-fns/locale";

interface TeacherScheduleTableProps {
  fixedJobs: FixedJob[];
  isLoading: boolean;
}

const DAYS_OF_WEEK: DayOfWeek[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY", 
  "FRIDAY",
  "SATURDAY",
  "SUNDAY"
];

const DAY_LABELS: Record<DayOfWeek, string> = {
  "MONDAY": "วันจันทร์",
  "TUESDAY": "วันอังคาร",
  "WEDNESDAY": "วันพุธ",
  "THURSDAY": "วันพฤหัสบดี",
  "FRIDAY": "วันศุกร์",
  "SATURDAY": "วันเสาร์",
  "SUNDAY": "วันอาทิตย์"
};

export function TeacherScheduleTable({ fixedJobs, isLoading }: TeacherScheduleTableProps) {
  // Organize schedule by day of week
  const scheduleByDay = useMemo(() => {
    const result: ScheduleByDay = {};
    
    // Initialize all days
    DAYS_OF_WEEK.forEach(day => {
      result[day] = [];
    });
    
    // Populate with jobs
    fixedJobs.forEach(job => {
      const day = job.time_slot_day.toUpperCase() as DayOfWeek;
      if (DAYS_OF_WEEK.includes(day)) {
        result[day].push(job);
      }
    });
    
    // Sort each day's jobs by start time
    DAYS_OF_WEEK.forEach(day => {
      result[day].sort((a, b) => {
        return a.time_slot_start_at.localeCompare(b.time_slot_start_at);
      });
    });
    
    return result;
  }, [fixedJobs]);

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">กำลังโหลดข้อมูล...</div>;
  }

  if (fixedJobs.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">ไม่พบข้อมูลตารางสอน</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
      {DAYS_OF_WEEK.map(day => (
        <Card key={day} className={`${scheduleByDay[day].length === 0 ? 'opacity-70' : ''}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-center">{DAY_LABELS[day]}</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {scheduleByDay[day].length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-2">ไม่มีคลาสเรียน</div>
            ) : (
              <div className="space-y-2">
                {scheduleByDay[day].map((job) => (
                  <div key={`${job.time_slot_id}-${job.id}`} className="border rounded-lg p-2 text-xs bg-card">
                    <div className="font-medium">
                      {job.time_slot_start_at.slice(0, 5)} - {job.time_slot_end_at.slice(0, 5)} น.
                    </div>
                    <div className="mt-1 text-muted-foreground">{job.branch}</div>
                    <div className="font-medium mt-1">{job.subjects || job.class_type}</div>
                    <div className="mt-1 text-xs line-clamp-1">{job.student_names}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
